import { Type, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PrintAreaSelector from '@components/PrintArea/PrintAreaSelector';
import PrintAreaCanvas from '@components/PrintArea/PrintAreaCanvas';
import LogoGallery from '@components/Logo/LogoGallery';
import LogoUploader from '@components/Logo/LogoUploader';
import EffectSelector from '@components/Effects/EffectSelector';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import { getDefaultModels } from '@utils/modelDiscovery';
import { getFontsByCategory } from '@config/fonts';
import './RightPanel.css';

export default function RightPanel() {
    const navigate = useNavigate();
    const {
        currentModel,
        setActivePrintArea,
        activePrintAreaId,
        printAreas,
        clearLogoInstances,
        logoInstances,
        textInstances,
        activeTextInstanceId,
        updateTextInstance,
        addTextInstance,
        setLoading,
        clearColorCustomizations
    } = useConfiguratorStore();

    // Get models list
    const models = getDefaultModels();

    const handleModelChange = (modelId: string) => {
        // Check if there are any logos placed on current model
        if (logoInstances.length > 0) {
            const confirmSwitch = confirm(
                `You have ${logoInstances.length} logo${logoInstances.length > 1 ? 's' : ''} placed on this model.\n\n` +
                'Switching models will remove all logos from the current model.\n\n' +
                'Do you want to continue?'
            );

            if (!confirmSwitch) {
                return; // User cancelled, don't switch models
            }
        }

        const selectedModel = models.find(m => m.id === modelId);
        if (selectedModel) {
            setLoading(true); // START LOADING IMMEDIATE

            // Use the router to drive the state change
            navigate(`/${modelId}`);

            // Clear active print area and instances when model changes manually
            setTimeout(() => {
                setActivePrintArea(null);
                clearLogoInstances();
                clearColorCustomizations();
            }, 50);
        }
    };

    // Auto-select first print area when print areas are first loaded
    useEffect(() => {
        // Check if current activePrintAreaId exists in the new print areas
        const activeExists = activePrintAreaId && printAreas.some(pa => pa.id === activePrintAreaId);

        // Auto-select first print area if:
        // 1. No print area is selected OR
        // 2. Selected print area doesn't exist in current model's print areas (model switched)
        if (printAreas.length > 0 && !activeExists) {
            const firstPrintArea = printAreas[0];
            if (firstPrintArea && firstPrintArea.id && firstPrintArea.id.trim()) {
                // Small delay to ensure component is fully mounted
                setTimeout(() => {
                    setActivePrintArea(firstPrintArea.id);
                }, 100);
            }
        }
    }, [printAreas]);

    return (
        <div className="right-panel">
            {/* Model Selector Section */}
            <div className="model-selector-section">
                <label className="section-title">Products</label>
                <select
                    className="model-selector-dropdown"
                    value={currentModel}
                    onChange={(e) => handleModelChange(e.target.value)}
                >
                    {models.map(model => (
                        <option key={model.id} value={model.id}>
                            {model.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Print Area Section */}
            <div className="print-area-section">
                <h2 className="section-title">Print Area</h2>

                {/* T-Shirt View Selector */}
                <PrintAreaSelector />

                {/* Design Canvas */}
                <PrintAreaCanvas />

                {/* Effect Selector (Contextual) */}
                <EffectSelector />
            </div>

            {/* Upload and Text Tools Section - Side by Side */}
            <div className="upload-text-grid">
                {/* Upload Section */}
                <div className="upload-section">
                    <LogoUploader />
                </div>

                {/* Text Tools Section */}
                <div className="text-tools-section">
                    <div className="text-tools-row">
                        {/* Add Text Button */}
                        <button
                            className="add-text-button"
                            title="Add Text"
                            onClick={() => {
                                // Calculate UV-aware centered position (same as LogoGallery)
                                const calculateCenteredPosition = (targetPrintAreaId: string) => {
                                    const targetPrintArea = printAreas.find(pa => pa.id === targetPrintAreaId);
                                    if (targetPrintArea?.uvBounds) {
                                        const { minU, maxU, minV, maxV, needsFlipY } = targetPrintArea.uvBounds;
                                        let centerU = (minU + maxU) / 2;
                                        let centerV = (minV + maxV) / 2;
                                        // Wrap to 0-1 range
                                        centerU = ((centerU % 1) + 1) % 1;
                                        centerV = ((centerV % 1) + 1) % 1;
                                        // Apply inversion flags
                                        const useInvertX = targetPrintArea.invertX ?? false;
                                        const useInvertY = targetPrintArea.invertY ?? needsFlipY ?? false;
                                        const x = useInvertX ? (1 - centerU) : centerU;
                                        const y = useInvertY ? centerV : (1 - centerV);
                                        return { x, y };
                                    }
                                    return { x: 0.5, y: 0.5 }; // Fallback
                                };

                                if (activePrintAreaId) {
                                    const pos = calculateCenteredPosition(activePrintAreaId);
                                    addTextInstance(activePrintAreaId, 'New Text', pos, { rotation: (currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') ? 90 : 0 });
                                } else if (printAreas.length > 0) {
                                    const pos = calculateCenteredPosition(printAreas[0].id);
                                    addTextInstance(printAreas[0].id, 'New Text', pos, { rotation: (currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') ? 90 : 0 });
                                }
                            }}
                        >
                            <Type size={18} />
                            <span className="plus-icon">+</span>
                        </button>

                        {/* Text Content Input removed as per request for in-canvas editing */}

                        {!activeTextInstanceId && (
                            <div style={{ flex: 1 }}></div> // Spacer
                        )}

                        {/* Font Family */}
                        <select
                            className="text-tool-select font-select"
                            value={activeTextInstanceId ? (textInstances.find(t => t.id === activeTextInstanceId)?.fontFamily || 'Roboto') : 'placeholder'}
                            onChange={(e) => {
                                if (activeTextInstanceId) {
                                    updateTextInstance(activeTextInstanceId, { fontFamily: e.target.value });
                                }
                            }}
                            disabled={!activeTextInstanceId}
                        >
                            <option value="placeholder" disabled hidden>Font</option>
                            {getFontsByCategory().map(group => (
                                <optgroup key={group.label} label={group.label}>
                                    {group.fonts.map(f => (
                                        <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
                                            {f.family}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>

                        {/* Font Size */}
                        <select
                            className="text-tool-select size-select"
                            value={activeTextInstanceId ? `${textInstances.find(t => t.id === activeTextInstanceId)?.fontSize || 24}px` : 'placeholder'}
                            onChange={(e) => {
                                if (activeTextInstanceId) {
                                    const size = parseInt(e.target.value);
                                    updateTextInstance(activeTextInstanceId, { fontSize: size });
                                }
                            }}
                            disabled={!activeTextInstanceId}
                        >
                            <option value="placeholder" disabled hidden>Size</option>
                            <option value="12px">12px</option>
                            <option value="14px">14px</option>
                            <option value="16px">16px</option>
                            <option value="18px">18px</option>
                            <option value="20px">20px</option>
                            <option value="24px">24px</option>
                            <option value="32px">32px</option>
                            <option value="48px">48px</option>
                            <option value="64px">64px</option>
                        </select>

                        {/* Unified Style Group: Color + Bold + Italic + Underline */}
                        <div className="style-group-container">
                            {/* Color Picker */}
                            <div className="style-group-item color-item" title="Text Color">
                                <input
                                    type="color"
                                    id="text-color-picker"
                                    aria-label="Text Color"
                                    className="text-color-input"
                                    value={activeTextInstanceId ? (textInstances.find(t => t.id === activeTextInstanceId)?.color || '#000000') : '#000000'}
                                    onChange={(e) => {
                                        if (activeTextInstanceId) {
                                            updateTextInstance(activeTextInstanceId, { color: e.target.value });
                                        }
                                    }}
                                    disabled={!activeTextInstanceId}
                                />
                                {/* Optional: Add icon overlay if needed, but color input works well */}
                            </div>

                            {/* Divider line vertical? - CSS will handle borders */}

                            {/* Format Buttons code */}
                            {(() => {
                                const activeInstance = activeTextInstanceId ? textInstances.find(t => t.id === activeTextInstanceId) : null;
                                const isBold = activeInstance?.fontStyle.includes('bold');
                                const isItalic = activeInstance?.fontStyle.includes('italic');
                                const isUnderline = activeInstance?.fontStyle.includes('underline');
                                const align = activeInstance?.textAlign || 'center';

                                const toggleStyle = (style: string) => {
                                    if (!activeInstance) return;
                                    const currentStyles = activeInstance.fontStyle || [];
                                    const newStyles = currentStyles.includes(style)
                                        ? currentStyles.filter(s => s !== style)
                                        : [...currentStyles, style];
                                    updateTextInstance(activeInstance.id, { fontStyle: newStyles });
                                };

                                const setAlignment = (align: 'left' | 'center' | 'right') => {
                                    if (!activeInstance) return;
                                    updateTextInstance(activeInstance.id, { textAlign: align });
                                };

                                return (
                                    <>
                                        <button
                                            className={`style-group-item format-btn ${isBold ? 'active' : ''}`}
                                            title="Bold"
                                            onClick={() => toggleStyle('bold')}
                                            disabled={!activeTextInstanceId}
                                        >B</button>
                                        <button
                                            className={`style-group-item format-btn ${isItalic ? 'active' : ''}`}
                                            title="Italic"
                                            onClick={() => toggleStyle('italic')}
                                            disabled={!activeTextInstanceId}
                                        >I</button>
                                        <button
                                            className={`style-group-item format-btn ${isUnderline ? 'active' : ''}`}
                                            title="Underline"
                                            onClick={() => toggleStyle('underline')}
                                            disabled={!activeTextInstanceId}
                                        >U</button>
                                        <button
                                            className={`style-group-item format-btn ${align === 'left' ? 'active' : ''}`}
                                            title="Align Left"
                                            onClick={() => setAlignment('left')}
                                            disabled={!activeTextInstanceId}
                                        ><AlignLeft size={14} /></button>
                                        <button
                                            className={`style-group-item format-btn ${align === 'center' ? 'active' : ''}`}
                                            title="Align Center"
                                            onClick={() => setAlignment('center')}
                                            disabled={!activeTextInstanceId}
                                        ><AlignCenter size={14} /></button>
                                        <button
                                            className={`style-group-item format-btn ${align === 'right' ? 'active' : ''}`}
                                            title="Align Right"
                                            onClick={() => setAlignment('right')}
                                            disabled={!activeTextInstanceId}
                                        ><AlignRight size={14} /></button>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                    <p className="text-tools-hint">
                        Click on T+ to add text and adjust font settings.
                    </p>
                </div>
            </div>

            {/* Uploads Gallery */}
            <div className="gallery-section">
                <h3 className="gallery-title">Uploads:</h3>
                <LogoGallery />
            </div>


        </div>
    );
}
